import { useCallback, useState } from "react";
import type { OidcApplication } from "../shared/types";
import type { ProviderView } from "./types";

export interface DashboardWarning {
  code: string;
  title: string;
  detail: string;
  applicationId?: string;
}

/** Dialog form state: one object seeded from the entity, patched field by field. */
export function usePatchDraft<T extends object>(
  initial: () => T,
): [T, (patch: Partial<T>) => void] {
  const [draft, setDraft] = useState(initial);
  const patch = useCallback(
    (values: Partial<T>) => setDraft((current) => ({ ...current, ...values })),
    [],
  );
  return [draft, patch];
}

export function formatDate(timestamp?: number): string {
  if (!timestamp) return "Never";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp * 1000));
}

export function splitList(text: string, separator: "," | "\n"): string[] {
  return text
    .split(separator)
    .map((value) => value.trim())
    .filter(Boolean);
}

export function dashboardWarnings(
  applications: OidcApplication[],
  provider?: ProviderView,
): DashboardWarning[] {
  const warnings: DashboardWarning[] = [];
  if (provider && provider.signingKeys.published < 2) {
    warnings.push({
      code: "single_signing_key",
      title: "No standby signing key",
      detail:
        "Only one public signing key is published. Rotation currently has no overlap safety.",
    });
  }
  for (const application of applications.filter(
    (candidate) => candidate.status === "active",
  )) {
    const target = { applicationId: application.clientId };
    if (application.tokenEndpointAuthMethod === "client_secret_post") {
      warnings.push({
        ...target,
        code: "legacy_client_auth",
        title: `${application.displayName} sends its secret in the body`,
        detail: "Use client_secret_basic when the relying party supports it.",
      });
    }
    if (application.loginMode === "fixed") {
      warnings.push({
        ...target,
        code: "fixed_account",
        title: `${application.displayName} uses a fixed account`,
        detail:
          "Every login resolves to one directory identity. Review this shared-account exception regularly.",
      });
    }
    if (
      application.environment === "production" &&
      application.redirectUris.some((value) => {
        try {
          const hostname = new URL(value).hostname;
          return ["localhost", "127.0.0.1", "[::1]"].includes(hostname);
        } catch {
          return false;
        }
      })
    ) {
      warnings.push({
        ...target,
        code: "production_localhost",
        title: `${application.displayName} has a local callback`,
        detail: "Remove development callbacks from production applications.",
      });
    }
    if (!application.lastTokenAt) {
      warnings.push({
        ...target,
        code: "never_used",
        title: `${application.displayName} has no token exchange yet`,
        detail:
          "Run an end-to-end login after saving the Cloudflare configuration.",
      });
    }
    if (!application.secretConfigured) {
      warnings.push({
        ...target,
        code: "missing_secret",
        title: `${application.displayName} has no usable credential`,
        detail: "Rotate its client secret before enabling this application.",
      });
    }
    if (
      application.tokenEndpointAuthMethod !== "none" &&
      application.secretConfigured &&
      !application.secretRevealable
    ) {
      warnings.push({
        ...target,
        code: "secret_reveal_unavailable",
        title: `${application.displayName} cannot reveal its current secret`,
        detail:
          "This credential predates encrypted storage. Rotate it once to enable controlled reveal.",
      });
    }
  }
  return warnings;
}
